import React, { useCallback, useEffect, useState } from "react";
import { fetchUserAttributes } from "aws-amplify/auth";
import { toast } from "react-toastify";
import PlatformAdminNav from "../../components/platform/PlatformAdminNav";
import PlatformAdminHeader from "../../components/platform/PlatformAdminHeader";
import {
  ClinicRevenue,
  PlatformRevenueAPI,
  RazorpayReconcileReport,
  RevenueReport,
} from "../../services/platformRevenue.service";
import { formatPaiseAsInr } from "../../utils/billing";

const WINDOWS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "1 year" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/** Razorpay reports created_at as Unix seconds. */
function formatEpoch(seconds: number | null): string {
  if (!seconds) return "-";
  return new Date(seconds * 1000).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const StatCard: React.FC<{ label: string; value: string; hint?: string; accent?: string }> = ({
  label, value, hint, accent,
}) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className={`mt-1 text-xl font-bold ${accent ?? "text-slate-900"}`}>{value}</p>
    {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
  </div>
);

/**
 * Two different questions, deliberately not merged into one number.
 *
 *   Revenue      = what we RECOGNISED. Built from the GST documents we issued,
 *                  and a document only exists once a capture was attributed.
 *   Unrecorded   = what we RECEIVED and never wrote down. Only Razorpay can
 *                  answer that, because a capture with no local row leaves
 *                  nothing for our own tables to disagree about.
 *
 * The second is shown first when it is non-zero. A clinic that paid and got
 * nothing outranks any revenue figure on this page.
 */
const RevenuePage: React.FC = () => {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState<RevenueReport | null>(null);
  const [loading, setLoading] = useState(true);

  const [adminEmail, setAdminEmail] = useState<string>("");

  const [recon, setRecon] = useState<RazorpayReconcileReport | null>(null);
  const [reconLoading, setReconLoading] = useState(true);
  const [reconError, setReconError] = useState<string | null>(null);

  const loadRevenue = useCallback(async (window: number) => {
    setLoading(true);
    try {
      setReport(await PlatformRevenueAPI.revenue(window));
    } catch {
      toast.error("Could not load revenue.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run on load rather than behind a button. A check nobody presses finds
  // nothing, which is how five refused deploys and a dropped top-up all went
  // unnoticed. A failure is shown, never swallowed into a clean-looking page.
  const loadRecon = useCallback(async (window: number) => {
    setReconLoading(true);
    setReconError(null);
    try {
      setRecon(await PlatformRevenueAPI.reconcileRazorpay(window));
    } catch (e: any) {
      setRecon(null);
      setReconError(
        e?.body?.detail ?? e?.detail ?? e?.message ?? "Could not reach Razorpay.",
      );
    } finally {
      setReconLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRevenue(days);
    loadRecon(days);
  }, [days, loadRevenue, loadRecon]);

  useEffect(() => {
    fetchUserAttributes()
      .then(attrs => setAdminEmail(attrs.email ?? ""))
      .catch(() => { /* header just renders without it */ });
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <PlatformAdminNav adminEmail={adminEmail} />

      <div className="max-w-7xl mx-auto px-8 py-8">
        <PlatformAdminHeader subtitle="PlatformAdmin only - what we received against what we recorded." />

        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <p className="text-xs text-slate-500">
            {report
              ? `${formatDate(report.window_start)} to ${formatDate(report.window_end)}`
              : "Loading…"}
          </p>
          <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
            {WINDOWS.map(w => (
              <button
                key={w.days}
                onClick={() => setDays(w.days)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  days === w.days
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {/* Money received and credited to nothing. Top of the page when non-zero. */}
        {reconLoading ? (
          <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
            Checking Razorpay for payments we have no record of…
          </div>
        ) : reconError ? (
          <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">
              Could not check Razorpay
            </p>
            <p className="text-xs text-amber-800 mt-1">
              {reconError} This page cannot tell you whether anything is missing until
              that check succeeds. The revenue below is still accurate for what we recorded.
            </p>
            <button
              onClick={() => loadRecon(days)}
              className="mt-3 px-3 py-1.5 text-xs font-semibold text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : recon && recon.unrecorded_count > 0 ? (
          <div className="mb-5 rounded-xl border-2 border-red-300 bg-red-50 p-4">
            <p className="text-sm font-bold text-red-900">
              {recon.unrecorded_count} payment{recon.unrecorded_count === 1 ? "" : "s"} captured
              by Razorpay with no record here, totalling {formatPaiseAsInr(recon.unrecorded_paise)}
            </p>
            <p className="text-xs text-red-800 mt-1">
              These clinics paid and received nothing. Take each payment id to the Razorpay
              dashboard to see what it was for, then credit or refund by hand. Nothing is
              applied automatically, because crediting from a guess is worse than the gap.
            </p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-red-900/70">
                    <th className="py-1.5 pr-4 font-semibold">Payment</th>
                    <th className="py-1.5 pr-4 font-semibold">Order</th>
                    <th className="py-1.5 pr-4 font-semibold text-right">Amount</th>
                    <th className="py-1.5 pr-4 font-semibold">Captured</th>
                    <th className="py-1.5 font-semibold">Notes present</th>
                  </tr>
                </thead>
                <tbody>
                  {recon.findings.map(f => (
                    <tr key={f.context.payment_id} className="border-t border-red-200">
                      <td className="py-1.5 pr-4 font-mono text-red-900">{f.context.payment_id}</td>
                      <td className="py-1.5 pr-4 font-mono text-red-900/80">
                        {f.context.order_id ?? "-"}
                      </td>
                      <td className="py-1.5 pr-4 text-right font-semibold text-red-900">
                        {formatPaiseAsInr(f.context.amount_paise ?? 0)}
                      </td>
                      <td className="py-1.5 pr-4 text-red-900/80">
                        {formatEpoch(f.context.created_at)}
                      </td>
                      <td className="py-1.5 text-red-900/80">
                        {f.context.notes_keys.length ? f.context.notes_keys.join(", ") : "none"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : recon ? (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-xs font-medium text-emerald-800">
              Every payment Razorpay captured in this window is recorded here.
            </p>
          </div>
        ) : null}

        {loading ? (
          <p className="text-sm text-slate-500">Loading revenue…</p>
        ) : !report ? (
          <p className="text-sm text-slate-500">No revenue data.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <StatCard
                label="Net revenue"
                value={formatPaiseAsInr(report.net_paise)}
                hint="Taxable value, excluding GST"
              />
              <StatCard
                label="Gross collected"
                value={formatPaiseAsInr(report.gross_paise)}
                hint="What hit the bank, including GST"
              />
              <StatCard
                label="GST collected"
                value={formatPaiseAsInr(report.gst_paise)}
                hint="Held for remittance, not revenue"
                accent="text-slate-600"
              />
              <StatCard
                label="Paying clinics"
                value={String(report.clinic_count)}
                hint="With at least one document issued"
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs text-slate-500">
                      <th className="px-4 py-2.5 font-semibold">Clinic</th>
                      <th className="px-4 py-2.5 font-semibold">GSTIN</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Net</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Subscription</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Credits</th>
                      <th className="px-4 py-2.5 font-semibold text-right">GST</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Gross</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.clinics.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-400">
                          No documents issued in this window.
                        </td>
                      </tr>
                    ) : (
                      report.clinics.map((c: ClinicRevenue) => (
                        <tr key={c.clinic_id} className="border-b border-slate-100 last:border-0">
                          <td className="px-4 py-2.5 font-medium text-slate-900">
                            {c.clinic_name ?? c.clinic_id}
                          </td>
                          <td className="px-4 py-2.5 text-xs font-mono text-slate-500">
                            {c.clinic_gstin ?? "-"}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-slate-900">
                            {formatPaiseAsInr(c.net_paise)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-600">
                            {formatPaiseAsInr(c.subscription_net_paise)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-600">
                            {formatPaiseAsInr(c.credit_topup_net_paise)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-500">
                            {formatPaiseAsInr(c.gst_paise)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-600">
                            {formatPaiseAsInr(c.gross_paise)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-500">
                            {c.document_count}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="mt-3 text-[11px] text-slate-400">
              Revenue is what we recognised: a GST document exists only once a payment was
              attributed to a clinic. Anything captured but unattributed is in the panel
              above, not in this table.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default RevenuePage;
